import { forwardRef, useState, type ReactNode } from "react";
import { Platform, TextInput, View, type ViewStyle } from "react-native";
import { Send } from "lucide-react-native";
import { colors } from "../theme/tokens";
import { FieldInput, type FieldInputProps } from "./FieldInput";
import { IconButton } from "./IconButton";

/** The pill is one line tall; a multi-line composer grows to this height. */
const PILL_HEIGHT = 44;
const MAX_MULTILINE_HEIGHT = 132;
/** Centres one line of text in the pill. */
const MULTILINE_PADDING = 11;

export type ChatComposerProps = Omit<
  FieldInputProps,
  "textRole" | "tone" | "layoutStyle" | "value" | "onChangeText"
> & {
  value: string;
  onChangeText: (text: string) => void;
  onSend: () => void;
  /** Whether there is something to send right now. */
  canSend: boolean;
  /** What a screen reader says for the send button. */
  sendLabel: string;
  /** Stable analytics identifier for the send button. */
  sendAction?: string;
  sendTestID?: string;
  /** Inside the pill before the text, such as an attach button. */
  leading?: ReactNode;
  style?: ViewStyle;
};

/**
 * The message composer shared by Ask Louise and the support conversation:
 * the frosted pill with the text and a send icon that warms when there is
 * something to send. `multiline` lets a long message grow the pill.
 */
export const ChatComposer = forwardRef<TextInput, ChatComposerProps>(
  function ChatComposer(
    {
      value,
      onChangeText,
      onSend,
      canSend,
      sendLabel,
      sendAction,
      sendTestID,
      leading,
      style,
      multiline = false,
      editable = true,
      onContentSizeChange,
      ...inputProps
    },
    ref,
  ) {
    const sendEnabled = canSend && editable;
    // A web text area keeps the height it is given, so a multi-line composer
    // there starts one line tall and follows its text up to the maximum.
    const growsOnWeb = multiline && Platform.OS === "web";
    const [webHeight, setWebHeight] = useState(PILL_HEIGHT);
    return (
      <View
        style={{
          flexDirection: "row",
          alignItems: multiline ? "flex-end" : "center",
          minHeight: PILL_HEIGHT,
          borderRadius: PILL_HEIGHT / 2,
          backgroundColor: "rgba(255,255,255,0.5)",
          borderWidth: 1,
          borderColor: "rgba(255,255,255,0.6)",
          paddingHorizontal: 16,
          gap: 10,
          ...style,
        }}
      >
        {leading ? (
          <View style={{ height: PILL_HEIGHT, justifyContent: "center" }}>
            {leading}
          </View>
        ) : null}
        <FieldInput
          {...inputProps}
          ref={ref}
          value={value}
          onChangeText={onChangeText}
          textRole="adaptive"
          multiline={multiline}
          editable={editable}
          onContentSizeChange={(event) => {
            if (growsOnWeb) {
              const height = Math.ceil(event.nativeEvent.contentSize.height);
              setWebHeight(
                Math.min(MAX_MULTILINE_HEIGHT, Math.max(PILL_HEIGHT, height)),
              );
            }
            onContentSizeChange?.(event);
          }}
          onSubmitEditing={multiline ? undefined : onSend}
          returnKeyType={multiline ? undefined : "send"}
          layoutStyle={{
            flex: 1,
            minWidth: 0,
            minHeight: PILL_HEIGHT,
            maxHeight: multiline ? MAX_MULTILINE_HEIGHT : undefined,
            paddingVertical: multiline ? MULTILINE_PADDING : 0,
            ...(growsOnWeb
              ? { height: value ? webHeight : PILL_HEIGHT }
              : null),
          }}
        />
        <View style={{ height: PILL_HEIGHT, justifyContent: "center" }}>
          <IconButton
            onPress={onSend}
            hitSlop={8}
            disabled={!sendEnabled}
            accessibilityLabel={sendLabel}
            action={sendAction}
            testID={sendTestID}
            size={16}
            disabledOpacity={1}
            pressedOpacity={1}
          >
            <Send
              size={16}
              color={sendEnabled ? colors.warm : colors.textFaint}
              strokeWidth={1.8}
            />
          </IconButton>
        </View>
      </View>
    );
  },
);
